import { useCallback, useEffect } from 'react';

import { Country } from '@dreryk/address-common-api';

import { useFormController } from '@dreryk/react-common';
import {
   ModalConfirmButton,
   ModalWidth,
   ShowModalEventInterceptor,
   ValidateFormEventInterceptor,
} from '@dreryk/react-components';

import { guardiansTranslationKeys } from '../../../translations';
import { GuardianDetailsFormBean } from '../GuardianDetailsModal/GuardianDetailsModal';
import { GuardianModalFormBean, GuardianModalFormController, StatutoryRepresentative } from '../GuardianModal.types';

import { GuardianAddressFormContent } from './GuardianAddressFormContent';
import { useGuardianCreateOrUpdateCommand } from './GuardianModalConfirmButton.hooks';
import { GuardianContactDataModalFormBean } from './GuardianModalConfirmButton.types';

type GuardianModalConfirmButtonProps = Pick<
   GuardianModalFormController,
   'setError' | 'getValue' | 'setValue' | 'trigger' | 'watch' | 'handleSubmit'
> & {
   readonly initialValues: GuardianDetailsFormBean | null;
   readonly guardianId?: number;
   readonly patientId?: number;
   readonly onSubmit?: (formData: GuardianModalFormBean) => void;
   readonly onSuccess?: (parameters: GuardianModalFormBean) => void;
};

const shouldShowModal = (currentFormValues: GuardianModalFormBean): boolean => {
   const { statutoryRepresentative, guardian } = currentFormValues;

   return Boolean(
      StatutoryRepresentative.YES === statutoryRepresentative &&
         (!guardian?.firstName ||
            !guardian.lastName ||
            !guardian.town ||
            !guardian.house ||
            !guardian.postcode ||
            !guardian.street ||
            !guardian.country)
   );
};

const GUARDIAN_CONTACT_DATA_FORM_INITIAL_VALUES: GuardianContactDataModalFormBean = {
   country: Country.DEFAULT,
   town: null,
   postcode: null,
   street: null,
   house: null,
   phoneNumber: null,
   emailAddress: null,
   flat: null,
};

// eslint-disable-next-line max-lines-per-function
export const GuardianModalConfirmButton = (props: GuardianModalConfirmButtonProps): JSX.Element => {
   const { initialValues, onSubmit, patientId } = props;
   const { register, handleSubmit, reset, watch } = useFormController<GuardianContactDataModalFormBean>({
      ...GUARDIAN_CONTACT_DATA_FORM_INITIAL_VALUES,
      ...(initialValues ?? {}),
   });

   useEffect(() => {
      if (initialValues) {
         reset({
            ...GUARDIAN_CONTACT_DATA_FORM_INITIAL_VALUES,
            ...initialValues,
         });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [initialValues]);

   const { executeAsync: submitGuardian } = useGuardianCreateOrUpdateCommand(
      props.patientId,
      props.guardianId,
      props.setError,
      props.onSuccess
   );

   const handleGuardianAddressFormSubmit = async (closeModal: VoidFunction): Promise<void> => {
      await handleSubmit(({ town, emailAddress, phoneNumber, ...address }: GuardianContactDataModalFormBean): void => {
         const guardian = props.getValue('guardian')!;

         props.setValue(
            'guardian',
            {
               ...guardian,
               emailAddress: emailAddress,
               phoneNumber: phoneNumber,
               town: town ?? null,
               flat: address.flat ?? null,
               street: address.street ?? null,
               postcode: address.postcode ?? null,
               house: address.house ?? null,
               country: address.country,
            } as any,
            { shouldDirty: true }
         );
         closeModal();
      })();
   };

   const handleConfirm = useCallback(
      // eslint-disable-next-line consistent-return
      async (formBean: GuardianModalFormBean): Promise<void> => {
         if (onSubmit) {
            onSubmit(formBean);
         }
         if (patientId || props.guardianId) {
            return submitGuardian(formBean);
         }
      },
      [submitGuardian, onSubmit, patientId, props.guardianId]
   );
   return (
      <ValidateFormEventInterceptor trigger={props.trigger}>
         <ShowModalEventInterceptor
            testId={'guardianAddress'}
            modalWidth={ModalWidth.HUGE}
            shouldShowModal={shouldShowModal(props.watch())}
            title={guardiansTranslationKeys.statutoryRepresentativeAddressData}
            confirmationButtonLabel={guardiansTranslationKeys.save}
            content={<GuardianAddressFormContent register={register} watch={watch} />}
            onSubmit={handleGuardianAddressFormSubmit}
            wrapInForm
         >
            <ModalConfirmButton label={guardiansTranslationKeys.save} onClick={props.handleSubmit(handleConfirm)} />
         </ShowModalEventInterceptor>
      </ValidateFormEventInterceptor>
   );
};
